import { For, Show, createEffect, createSignal } from "solid-js";

import {
  useCreateSavedView,
  useDeleteSavedView,
  useMakeDefaultSavedView,
  useSavedViews,
  useUpdateSavedView,
} from "~/lib/hooks";
import type { SavedViewEntityType, SavedViewRead } from "~/lib/types";

type Props = {
  wsKey: () => string;
  entityType: () => SavedViewEntityType;
  // Required when entityType() === "custom_record"; ignored otherwise.
  // Lets the same view name (e.g. "Active") differentiate across objects
  // so "Active assets" and "Active subscriptions" can coexist.
  customObjectDefinitionId?: () => string | null;
  // Current filter state for this list page. Compared against the active
  // view's filters to detect "dirty" so the Save dropdown can offer
  // "Update existing" vs "Save as new".
  currentFilters: () => Record<string, unknown>;
  // Called when the user picks a view — write its filters into the page's
  // local filter signals.
  onApply: (filters: Record<string, unknown>) => void;
  // Optional: clear the page's filters (used when the user picks "All").
  onClear: () => void;
};

// Drop nulls/undefined/empty strings from a filter object so equality
// comparisons between saved JSON and the live filter signal don't get
// derailed by absent vs explicit-empty differences.
function compact(f: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(f)) {
    if (v === null || v === undefined) continue;
    if (typeof v === "string" && v === "") continue;
    out[k] = v;
  }
  return out;
}

function sameFilters(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const ca = compact(a);
  const cb = compact(b);
  const aKeys = Object.keys(ca).sort();
  const bKeys = Object.keys(cb).sort();
  if (aKeys.length !== bKeys.length) return false;
  for (let i = 0; i < aKeys.length; i++) {
    if (aKeys[i] !== bKeys[i]) return false;
    if (JSON.stringify(ca[aKeys[i]]) !== JSON.stringify(cb[bKeys[i]])) return false;
  }
  return true;
}

export function SavedViewsBar(props: Props) {
  const list = useSavedViews(props.wsKey, props.entityType, props.customObjectDefinitionId);
  const createMut = useCreateSavedView(props.wsKey);
  const updateMut = useUpdateSavedView(props.wsKey);
  const deleteMut = useDeleteSavedView(props.wsKey);
  const defaultMut = useMakeDefaultSavedView(props.wsKey);

  const [activeId, setActiveId] = createSignal<string | null>(null);
  const [autoApplied, setAutoApplied] = createSignal(false);

  // Apply the default view once on first load.
  createEffect(() => {
    if (autoApplied()) return;
    const items = list.data?.items;
    if (!items) return;
    const def = items.find((v) => v.is_default);
    if (def) {
      props.onApply(def.filters);
      setActiveId(def.id);
    }
    setAutoApplied(true);
  });

  const activeView = (): SavedViewRead | null => {
    const id = activeId();
    if (!id) return null;
    return list.data?.items.find((v) => v.id === id) ?? null;
  };

  const isDirty = () => {
    const av = activeView();
    if (!av) return Object.keys(compact(props.currentFilters())).length > 0;
    return !sameFilters(av.filters, props.currentFilters());
  };

  const pick = (v: SavedViewRead) => {
    setActiveId(v.id);
    props.onApply(v.filters);
  };

  const clear = () => {
    setActiveId(null);
    props.onClear();
  };

  const saveAsNew = async () => {
    const name = window.prompt("Name this view:");
    if (!name) return;
    try {
      const sv = await createMut.mutateAsync({
        entity_type: props.entityType(),
        custom_object_definition_id:
          props.entityType() === "custom_record"
            ? (props.customObjectDefinitionId?.() ?? null)
            : null,
        name: name.trim(),
        filters: compact(props.currentFilters()),
        is_default: false,
      });
      setActiveId(sv.id);
    } catch (err) {
      window.alert((err as Error).message);
    }
  };

  const updateActive = async () => {
    const av = activeView();
    if (!av) return;
    try {
      await updateMut.mutateAsync({
        svId: av.id,
        version: av.version,
        body: { filters: compact(props.currentFilters()) },
      });
    } catch (err) {
      window.alert((err as Error).message);
    }
  };

  const renameActive = async () => {
    const av = activeView();
    if (!av) return;
    const next = window.prompt("Rename view:", av.name);
    if (!next || next.trim() === av.name) return;
    try {
      await updateMut.mutateAsync({
        svId: av.id,
        version: av.version,
        body: { name: next.trim() },
      });
    } catch (err) {
      window.alert((err as Error).message);
    }
  };

  const deleteActive = async () => {
    const av = activeView();
    if (!av) return;
    if (!window.confirm(`Delete view "${av.name}"?`)) return;
    try {
      await deleteMut.mutateAsync({ svId: av.id });
      clear();
    } catch (err) {
      window.alert((err as Error).message);
    }
  };

  const makeActiveDefault = async () => {
    const av = activeView();
    if (!av) return;
    try {
      await defaultMut.mutateAsync({ svId: av.id });
    } catch (err) {
      window.alert((err as Error).message);
    }
  };

  const items = () => list.data?.items ?? [];

  return (
    <div class="flex items-center gap-2 flex-wrap">
      <details class="dropdown">
        <summary class="btn btn-sm gap-1 list-none">
          <span class="opacity-70">View:</span>
          <span class="font-medium">
            <Show when={activeView()} fallback="All">
              {(av) => (
                <>
                  {av().name}
                  <Show when={av().is_default}>
                    <span class="badge badge-primary badge-xs ml-1">★</span>
                  </Show>
                  <Show when={isDirty()}>
                    <span class="badge badge-warning badge-xs ml-1" title="Filters changed">
                      modified
                    </span>
                  </Show>
                </>
              )}
            </Show>
          </span>
          <span aria-hidden="true">▾</span>
        </summary>
        <ul class="menu dropdown-content bg-base-100 rounded-box z-30 w-64 p-2 shadow border border-base-300 mt-1">
          <li>
            <button type="button" onClick={clear} class={activeId() ? "" : "active"}>
              All (no view)
            </button>
          </li>
          <Show when={items().length > 0}>
            <li class="menu-title text-xs uppercase opacity-50 pt-2">Your views</li>
            <For each={items()}>
              {(v) => (
                <li>
                  <button
                    type="button"
                    onClick={() => pick(v)}
                    class={`flex items-center gap-2 ${activeId() === v.id ? "active" : ""}`}
                  >
                    <span class="flex-1 truncate">{v.name}</span>
                    <Show when={v.is_default}>
                      <span class="badge badge-primary badge-xs">★</span>
                    </Show>
                  </button>
                </li>
              )}
            </For>
          </Show>
          <li class="border-t border-base-300 mt-2 pt-2">
            <button type="button" onClick={saveAsNew}>
              Save current filters as…
            </button>
          </li>
          <Show when={activeView()}>
            {(av) => (
              <>
                <Show when={isDirty()}>
                  <li>
                    <button type="button" onClick={updateActive}>
                      Update "{av().name}" with current filters
                    </button>
                  </li>
                </Show>
                <Show when={!av().is_default}>
                  <li>
                    <button type="button" onClick={makeActiveDefault}>
                      Make "{av().name}" my default
                    </button>
                  </li>
                </Show>
                <li>
                  <button type="button" onClick={renameActive}>
                    Rename
                  </button>
                </li>
                <li>
                  <button type="button" onClick={deleteActive} class="text-error">
                    Delete
                  </button>
                </li>
              </>
            )}
          </Show>
        </ul>
      </details>
    </div>
  );
}
